import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Slider from "react-slick";
import { useAppSelector } from "../utils/hooks";
import LightboxVideoPlayer from "./LightboxVideoPlayer";
import { isHlsUrl } from "../utils/mediaPlayUrl";
import { useLightboxZoom } from "../hooks/useLightboxZoom";

function ReactSlickCarousel() {
  const image_list = useAppSelector((state) => state.ViewManager);
  const slider = useRef<Slider | null>(null);

  const slides = Array.isArray(image_list?.image_src)
    ? image_list.image_src.filter(
        (src): src is string => !!src && typeof src === "string",
      )
    : [];
  const hasMultipleSlides = slides.length >= 2;

  const startIndex =
    typeof image_list.currentIndex === "number" &&
    image_list.currentIndex >= 0 &&
    image_list.currentIndex < slides.length
      ? image_list.currentIndex
      : 0;

  const [currentSlide, setCurrentSlide] = useState(startIndex);
  const [controlsLocked, setControlsLocked] = useState(false);
  // Hit-test on the whole carousel shell (slick clones break per-slide refs)
  const { stageRef, scale, resetZoom } = useLightboxZoom(true);

  useEffect(() => {
    setCurrentSlide(startIndex);
    resetZoom();
  }, [startIndex, resetZoom]);

  useEffect(() => {
    resetZoom();
  }, [currentSlide, resetZoom]);

  const zoomed = scale > 1.01;
  const allowSwipe = !zoomed && !controlsLocked;

  const atStart = currentSlide <= 0;
  const atEnd = currentSlide >= slides.length - 1;

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    autoplay: false,
    slidesToScroll: 1,
    initialSlide: startIndex,
    arrows: false,
    adaptiveHeight: false,
    swipe: allowSwipe,
    draggable: allowSwipe,
    touchMove: allowSwipe,
    afterChange: (index: number) => setCurrentSlide(index),
  };

  const pathForExt = (url: string) => {
    try {
      return url.split(/[?#]/)[0] || url;
    } catch {
      return url;
    }
  };

  const isVideo = (url: string) =>
    isHlsUrl(url) ||
    /\.(mp4|webm|ogg|mov|m4v|3gp|mkv|avi)$/i.test(pathForExt(url));

  useEffect(() => {
    if (slider.current && typeof image_list.currentIndex === "number") {
      slider.current.slickGoTo(image_list.currentIndex, true);
    }
  }, [image_list.currentIndex]);

  if (slides.length === 0) {
    return null;
  }

  const mediaTransform = {
    transform: `scale(${scale})`,
    transformOrigin: "center center",
    transition: scale === 1 ? "transform 160ms ease-out" : "none",
  } as const;

  const videoClassName =
    "media-lightbox-item max-h-[min(75vh,820px)] max-w-[min(92vw,1400px)] w-auto rounded-lg bg-black object-contain shadow-lg";

  const slideElements = React.Children.toArray(
    slides.map((src, index) => {
      const active = index === currentSlide;

      return (
        <div className="slick-slide-media-wrap" key={`${src}-${index}`}>
          {isVideo(src) ? (
            active ? (
              <div
                className="lightbox-carousel-player"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <LightboxVideoPlayer
                  src={src}
                  className={videoClassName}
                  mediaStyle={mediaTransform}
                  onControlsGestureChange={setControlsLocked}
                />
              </div>
            ) : (
              <div className="flex h-[min(60vh,520px)] w-[min(80vw,720px)] items-center justify-center rounded-lg bg-black/40" />
            )
          ) : (
            <div className="flex max-h-[min(85vh,900px)] max-w-[min(92vw,1400px)] items-center justify-center overflow-visible">
              <img
                className="media-lightbox-item rounded-lg object-contain shadow-lg"
                src={src}
                alt={`Media ${index + 1}`}
                draggable={false}
                style={active ? mediaTransform : undefined}
              />
            </div>
          )}
        </div>
      );
    }),
  );

  return (
    <div
      ref={stageRef}
      className="slider-container relative h-[min(85vh,900px)] w-screen max-w-none !overflow-visible"
    >
      <Slider ref={slider} {...settings}>
        {slideElements}
      </Slider>

      {hasMultipleSlides && !atStart && (
        <button
          type="button"
          aria-label="Previous"
          onClick={() => slider.current?.slickPrev()}
          className="custom-prev-arrow bg-lightTextColor !text-darkTextColor absolute grid h-11 w-11 cursor-pointer place-content-center rounded-full hover:bg-opacity-70"
        >
          <FiChevronLeft className="h-7 w-7" />
        </button>
      )}
      {hasMultipleSlides && !atEnd && (
        <button
          type="button"
          aria-label="Next"
          onClick={() => slider.current?.slickNext()}
          className="custom-next-arrow bg-lightTextColor !text-darkTextColor absolute grid h-11 w-11 cursor-pointer place-content-center rounded-full hover:bg-opacity-70"
        >
          <FiChevronRight className="h-7 w-7" />
        </button>
      )}
    </div>
  );
}

export default ReactSlickCarousel;
